import { FC, useState } from 'react';

import './DeleteSaleDialog.css';
import { formatDollars } from '../../shared/functions/formatter';
import { Button, Dialog, Field, Input, Select } from '../../ui';

/**
 * Confirmation before a transaction is deleted.
 *
 * The prompt is deliberately explicit rather than a bare `confirm()`: deleting a
 * sale removes money from the day's totals, the reports and the CSV, so the
 * operator is shown exactly which transaction is about to go and is asked to say
 * why. The reason is required because it becomes the audit record.
 *
 * The panel, backdrop, focus and Escape handling are the shared Dialog's; this
 * file owns only the transaction summary and the reason field.
 */

/** Common causes, so the usual case is one click and a confirm. */
const PRESET_REASONS = ['Rang up by mistake', 'Customer cancelled', 'Duplicate entry', 'Test transaction'] as const;

const OTHER = 'Other';

type DeleteSaleDialogProps = {
  orderRef: string;
  total: number;
  itemSummary: string;
  when: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
};

const DeleteSaleDialog: FC<DeleteSaleDialogProps> = ({ orderRef, total, itemSummary, when, busy = false, onCancel, onConfirm }) => {
  const [choice, setChoice] = useState<string>(PRESET_REASONS[0]);
  const [custom, setCustom] = useState('');

  const usingOther = choice === OTHER;
  const reason = (usingOther ? custom : choice).trim();
  const canDelete = reason.length > 0 && !busy;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title="Delete this transaction?"
      description="This removes the sale from the day's totals, the reports and the CSV export. It cannot be undone, but the removal is recorded with your name and the reason below."
      tone="danger"
      actions={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            cancel
          </Button>
          <Button variant="destructive" onClick={() => onConfirm(reason)} disabled={!canDelete}>
            {busy ? 'deleting…' : 'delete transaction'}
          </Button>
        </>
      }
    >
      <dl className="deleteSaleSummary">
        <div>
          <dt>Reference</dt>
          <dd className="deleteSaleRef">{orderRef}</dd>
        </div>
        <div>
          <dt>When</dt>
          <dd>{when}</dd>
        </div>
        <div>
          <dt>Items</dt>
          <dd>{itemSummary}</dd>
        </div>
        <div>
          <dt>Total</dt>
          <dd className="money">{formatDollars(total)}</dd>
        </div>
      </dl>

      <Field label="Reason" id="delete-sale-reason">
        <Select value={choice} onChange={(event) => setChoice(event.target.value)} disabled={busy}>
          {PRESET_REASONS.map((preset) => (
            <option key={preset} value={preset}>
              {preset}
            </option>
          ))}
          <option value={OTHER}>{OTHER}…</option>
        </Select>
      </Field>

      {usingOther && (
        <Input
          value={custom}
          autoFocus
          maxLength={255}
          placeholder="Why is this being deleted?"
          aria-label="Reason for deleting this transaction"
          onChange={(event) => setCustom(event.target.value)}
        />
      )}
    </Dialog>
  );
};

export default DeleteSaleDialog;
